/** The Email page's dialogs: create, rename, reset password, delete, and the alias and forwarding forms. */
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Alert, Button, ErrorAlert, Field, Form, Input, Modal, Select } from "@/components/ui";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { domainsApi } from "@/lib/domains";
import { useFormDraft } from "@/lib/form-drafts";
import {
  LOCAL_PART_HELP,
  LOCAL_PART_MAX,
  LOCAL_PART_RE,
  dependents,
  emailApi,
  isDnsProblem,
  isPlanProblem,
  shownOnAField,
  withFieldError,
  type EmailActionResult,
  type EmailAvailability,
  type EmailItem,
} from "@/lib/mailboxes";

import { useEmailMutation } from "./actions";

/** A refusal that is fixed by changing the plan comes with the way there. */
function PlanLink({ error }: { error: unknown }) {
  if (isDnsProblem(error)) {
    return (
      <p className="text-sm">
        <Link to="/email" className="font-semibold text-brand-700 underline">
          Check this domain's email DNS records
        </Link>
      </p>
    );
  }
  if (!isPlanProblem(error)) return null;
  return (
    <p className="text-sm">
      <Link to="/billing/plans" className="font-semibold text-brand-700 underline">
        See plans and upgrade
      </Link>
    </p>
  );
}

function Footer({
  onClose,
  form,
  submit,
  pending,
  danger,
  disabledReason,
}: {
  onClose: () => void;
  form: string;
  submit: string;
  pending: boolean;
  danger?: boolean;
  disabledReason?: ReactNode;
}) {
  return (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        type="submit"
        form={form}
        variant={danger ? "danger" : "primary"}
        loading={pending}
        disabledReason={disabledReason}
      >
        {submit}
      </Button>
    </>
  );
}

const closeOn = (onClose: () => void) => (_: EmailActionResult) => onClose();

// ── create a mailbox ─────────────────────────────────────────────────────────
export function CreateMailboxDialog({
  availability,
  onClose,
}: {
  availability: EmailAvailability;
  onClose: () => void;
}) {
  const { orgId } = useOrganization();
  const domains = useQuery({
    queryKey: ["email", orgId, "domains"],
    queryFn: () => domainsApi.list(orgId, { page_size: 100 }),
  });
  const eligible = (domains.data?.domains ?? []).filter((d) => d.status === "active");
  const [domainId, setDomainId] = useState("");
  const chosen = eligible.find((d) => d.id === domainId) ?? eligible[0];
  const [local, setLocal] = useState("");
  const [name, setName] = useState("");
  // PHP-3874: kept as a draft once the domains are in (so the domain it settles on is where the form started).
  // The form never collects a password: the mailbox's is generated and shown once, elsewhere.
  const kept = useFormDraft(
    "mailboxes.create",
    { domainId: chosen?.id ?? "", local, name },
    {
      ready: !domains.isPending,
    },
  );
  const create = useEmailMutation(
    (org, vars: { domain_id: string; local_part: string; display_name: string }, key) =>
      emailApi.createMailbox(org, { ...vars, display_name: vars.display_name || null }, key),
    {
      done: "Mailbox created",
      onResult: (result) => {
        kept.clear();
        closeOn(onClose)(result);
      },
    },
  );
  const none = !domains.isPending && !eligible.length;
  return (
    <Modal
      open
      onClose={onClose}
      title="Create a mailbox"
      subtitle={`${availability.mailboxes_used} of ${availability.mailbox_limit} mailboxes used on ${availability.plan_name}`}
      footer={
        <Footer
          onClose={onClose}
          form="create-mailbox"
          submit="Create mailbox"
          pending={create.isPending}
          disabledReason={
            none ? "You need an active domain first." : !chosen ? "Loading your domains…" : undefined
          }
        />
      }
    >
      <Form
        id="create-mailbox"
        className="space-y-4"
        error={withFieldError(create.error)}
        validate={() => ({
          local_part: local.trim() && !LOCAL_PART_RE.test(local.trim()) ? LOCAL_PART_HELP : false,
        })}
        onSubmit={(e) => {
          e.preventDefault();
          if (!chosen) return;
          create.mutate({ domain_id: chosen.id, local_part: local.trim(), display_name: name.trim() });
        }}
      >
        {!shownOnAField(create.error) && <ErrorAlert error={create.error} />}
        <PlanLink error={create.error} />
        <DraftOffer
          draft={kept}
          onContinue={(d) => {
            if (eligible.some((x) => x.id === d.domainId)) setDomainId(d.domainId);
            setLocal(d.local ?? "");
            setName(d.name ?? "");
          }}
        />
        {domains.isError && <ErrorAlert error={domains.error} />}
        {none && (
          <Alert tone="info">
            Mailboxes live on a domain you own.{" "}
            <Link to="/domains/find" className="font-semibold underline">
              Find a domain
            </Link>{" "}
            first.
          </Alert>
        )}
        <Field label="Domain" name="domain_id" required>
          {(p) => (
            <Select
              {...p}
              value={chosen?.id ?? ""}
              onChange={(e) => setDomainId(e.target.value)}
              disabled={!eligible.length}
            >
              {eligible.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.fqdn}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Address" name="local_part" required hint="The part before the @">
          {(p) => (
            <div className="flex items-center gap-2">
              <Input
                {...p}
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                maxLength={LOCAL_PART_MAX}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="hello"
              />
              <span className="shrink-0 text-sm text-fg-muted">@{chosen?.fqdn ?? "…"}</span>
            </div>
          )}
        </Field>
        <Field label="Name shown on messages" name="display_name" optional>
          {(p) => (
            <Input
              {...p}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoComplete="off"
              placeholder="Salt & Bloom"
            />
          )}
        </Field>
        <DraftSavedNote savedAt={kept.savedAt} />
      </Form>
    </Modal>
  );
}

// ── rename a mailbox ─────────────────────────────────────────────────────────
export function RenameDialog({ item, onClose }: { item: EmailItem; onClose: () => void }) {
  const [name, setName] = useState(item.display_name ?? "");
  const rename = useEmailMutation(
    (org, display_name: string, key) => emailApi.renameMailbox(org, item.id, display_name, key),
    { done: "Name saved", onResult: closeOn(onClose) },
  );
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename mailbox"
      subtitle={item.address}
      footer={
        <Footer onClose={onClose} form="rename-mailbox" submit="Save name" pending={rename.isPending} />
      }
    >
      <Form
        id="rename-mailbox"
        className="space-y-4"
        error={withFieldError(rename.error)}
        onSubmit={(e) => {
          e.preventDefault();
          rename.mutate(name.trim());
        }}
      >
        {!shownOnAField(rename.error) && <ErrorAlert error={rename.error} />}
        <Field label="Name shown on messages" name="display_name">
          {(p) => (
            <Input
              {...p}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoComplete="off"
            />
          )}
        </Field>
        <p className="text-xs text-fg-subtle">The new name appears once the email provider confirms it.</p>
      </Form>
    </Modal>
  );
}

// ── reset a mailbox's password ───────────────────────────────────────────────
export function ResetDialog({ item, onClose }: { item: EmailItem; onClose: () => void }) {
  const reset = useEmailMutation((org, _: void, key) => emailApi.act(org, item.id, "reset", key), {
    done: "Password reset requested",
    onResult: closeOn(onClose),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Reset password"
      subtitle={item.address}
      footer={
        <Footer onClose={onClose} form="reset-mailbox" submit="Reset password" pending={reset.isPending} />
      }
    >
      <Form
        id="reset-mailbox"
        className="space-y-4"
        error={reset.error}
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate();
        }}
      >
        <ErrorAlert error={reset.error} />
        <p className="text-sm text-fg">
          Our email provider will send reset instructions to the mailbox&rsquo;s owner. CortexGrip never sees,
          stores or e-mails the password.
        </p>
        <p className="text-sm text-fg-muted">
          The people who manage email on this business are told that a reset was requested. If nobody expects
          this, contact support.
        </p>
      </Form>
    </Modal>
  );
}

// ── delete a mailbox, an alias or a forwarding rule ──────────────────────────
function consequence(item: EmailItem): string {
  if (item.kind === "forward")
    return `Mail will no longer be forwarded to ${item.target ?? "that address"}. Nothing already forwarded is affected.`;
  if (item.kind === "alias")
    return `Mail sent to ${item.address} will bounce. The mailbox it delivers to is not affected.`;
  return `${item.address} and the mail in it will be deleted. Mail sent to it will bounce. You can recover the mailbox for a limited time, but not its aliases or forwarding rules.`;
}

export function DeleteDialog({
  item,
  recoveryDays,
  onClose,
  onDeleted,
}: {
  item: EmailItem;
  recoveryDays: number;
  onClose: () => void;
  onDeleted: (item: EmailItem) => void;
}) {
  const remove = useEmailMutation(
    (org, withDependents: boolean, key) => emailApi.remove(org, item.id, key, withDependents),
    {
      done: item.kind === "mailbox" ? "Mailbox deleted" : "Removed",
      onResult: (result) => {
        if (result.outcome === "succeeded") onDeleted(result.item);
        onClose();
      },
    },
  );
  const also = dependents(remove.error);
  const label = item.kind === "forward" ? `forwarding to ${item.target}` : item.address;
  return (
    <Modal
      open
      onClose={onClose}
      title={
        item.kind === "mailbox"
          ? "Delete mailbox"
          : item.kind === "alias"
            ? "Remove alias"
            : "Remove forwarding"
      }
      subtitle={label}
      footer={
        <Footer
          onClose={onClose}
          form="delete-item"
          submit={also ? "Delete all" : item.kind === "mailbox" ? "Delete mailbox" : "Remove"}
          pending={remove.isPending}
          danger
        />
      }
    >
      <Form
        id="delete-item"
        className="space-y-4"
        error={remove.error}
        onSubmit={(e) => {
          e.preventDefault();
          // The first answer said what else would go; confirming it is a new request with a new key.
          remove.mutate(also !== null);
        }}
      >
        {!also && <ErrorAlert error={remove.error} />}
        {also && (
          <Alert tone="warning">
            <p>{remove.error instanceof Error ? remove.error.message : ""}</p>
          </Alert>
        )}
        <p className="text-sm text-fg">{consequence(item)}</p>
        {item.kind === "mailbox" && (
          <p className="text-sm text-fg-muted">
            It can be recovered for {recoveryDays} days after you delete it.
          </p>
        )}
      </Form>
    </Modal>
  );
}

// ── aliases ──────────────────────────────────────────────────────────────────
export function AliasDialog({
  mailbox,
  alias,
  mailboxes,
  onClose,
}: {
  mailbox: EmailItem;
  /** An alias to move to another mailbox; absent: add one. */
  alias?: EmailItem;
  /** The mailboxes an alias may deliver to (same domain, active or suspended). */
  mailboxes: EmailItem[];
  onClose: () => void;
}) {
  const [local, setLocal] = useState("");
  const [to, setTo] = useState(mailbox.id);
  const add = useEmailMutation(
    (org, part: string, key) => emailApi.createAlias(org, { mailbox_id: mailbox.id, local_part: part }, key),
    { done: "Alias created", onResult: closeOn(onClose) },
  );
  const move = useEmailMutation(
    (org, mailbox_id: string, key) => emailApi.moveAlias(org, alias?.id ?? "", mailbox_id, key),
    { done: "Alias moved", onResult: closeOn(onClose) },
  );
  const m = alias ? move : add;
  return (
    <Modal
      open
      onClose={onClose}
      title={alias ? "Move alias" : "Add an alias"}
      subtitle={alias ? alias.address : mailbox.address}
      footer={
        <Footer
          onClose={onClose}
          form="alias-form"
          submit={alias ? "Move alias" : "Add alias"}
          pending={m.isPending}
        />
      }
    >
      <Form
        id="alias-form"
        className="space-y-4"
        error={withFieldError(m.error)}
        validate={() =>
          alias
            ? undefined
            : { local_part: local.trim() && !LOCAL_PART_RE.test(local.trim()) ? LOCAL_PART_HELP : false }
        }
        onSubmit={(e) => {
          e.preventDefault();
          if (alias) move.mutate(to);
          else add.mutate(local.trim());
        }}
      >
        {!shownOnAField(m.error) && <ErrorAlert error={m.error} />}
        {alias ? (
          <Field label="Deliver to" name="mailbox_id" required>
            {(p) => (
              <Select {...p} value={to} onChange={(e) => setTo(e.target.value)}>
                {mailboxes.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.address}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field
            label="Alias"
            name="local_part"
            required
            hint={`Mail to this address is delivered to ${mailbox.address}.`}
          >
            {(p) => (
              <div className="flex items-center gap-2">
                <Input
                  {...p}
                  value={local}
                  onChange={(e) => setLocal(e.target.value)}
                  maxLength={LOCAL_PART_MAX}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="info"
                />
                <span className="shrink-0 text-sm text-fg-muted">@{mailbox.fqdn}</span>
              </div>
            )}
          </Field>
        )}
      </Form>
    </Modal>
  );
}

// ── forwarding ───────────────────────────────────────────────────────────────
export function ForwardDialog({
  mailbox,
  rule,
  onClose,
}: {
  mailbox: EmailItem;
  /** A rule to change; absent: add one. */
  rule?: EmailItem;
  onClose: () => void;
}) {
  const [target, setTarget] = useState(rule?.target ?? "");
  const add = useEmailMutation(
    (org, to: string, key) => emailApi.createForward(org, { mailbox_id: mailbox.id, target: to }, key),
    { done: "Forwarding added", onResult: closeOn(onClose) },
  );
  const change = useEmailMutation(
    (org, to: string, key) => emailApi.changeForward(org, rule?.id ?? "", to, key),
    { done: "Forwarding changed", onResult: closeOn(onClose) },
  );
  const m = rule ? change : add;
  return (
    <Modal
      open
      onClose={onClose}
      title={rule ? "Change forwarding" : "Forward mail"}
      subtitle={mailbox.address}
      footer={
        <Footer
          onClose={onClose}
          form="forward-form"
          submit={rule ? "Save forwarding" : "Add forwarding"}
          pending={m.isPending}
        />
      }
    >
      <Form
        id="forward-form"
        className="space-y-4"
        error={withFieldError(m.error)}
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate(target.trim());
        }}
      >
        {!shownOnAField(m.error) && <ErrorAlert error={m.error} />}
        <Field
          label="Forward to"
          name="target"
          required
          hint="A copy of every message sent to this mailbox goes to this address."
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="you@example.com"
            />
          )}
        </Field>
        <p className="text-xs text-fg-subtle">
          Forwarding sends mail outside your domain, so the people who manage email on this business are
          always told when a rule is added, changed or removed.
        </p>
      </Form>
    </Modal>
  );
}
